import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Menu, X, Headphones, LogOut, ChevronDown } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import UserTierIndicator from './UserTierIndicator';

const HeaderContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(10, 10, 10, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0 2rem;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    padding: 0 1rem;
    height: 70px;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #ff6b35;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #ff8c5a;
  }

  .logo-text {
    @media (max-width: 768px) {
      display: none;
    }
  }

  @media (max-width: 768px) {
    font-size: 1.2rem;
    gap: 0.25rem;
  }
`;

const MobileMenuOverlay = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 10, 10, 0.98);
  backdrop-filter: blur(20px);
  z-index: 99998;
  
  @media (min-width: 769px) {
    display: none !important;
  }
`;

const MobileMenuCloseButton = styled.button`
  position: fixed;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 48px;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
  pointer-events: auto;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  &:active {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(0.95);
  }
  
  svg {
    width: 24px;
    height: 24px;
    pointer-events: none;
  }
  
  @media (min-width: 769px) {
    display: none !important;
  }
`;

const Nav = styled.nav.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'isMobile',
})`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (min-width: 769px) {
    &.mobile-nav {
      display: none !important;
    }
    
    .mobile-user-section {
      display: none !important;
    }
  }

  @media (max-width: 768px) {
    /* Desktop nav inside NavAndUserContainer should be hidden on mobile */
    &:not(.mobile-nav) {
      display: none !important;
    }
    
    /* Mobile nav */
    &.mobile-nav {
      display: ${props => props.isOpen ? 'flex' : 'none'} !important;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: transparent;
      flex-direction: column;
      padding: 80px 1.5rem 2rem 1.5rem;
      gap: 0;
      z-index: 99999;
      overflow-y: auto;
      width: 100%;
      box-sizing: border-box;
      pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
      
      .mobile-user-section {
        display: flex !important;
      }
    }
  }
`;

const NavLink = styled(Link).withConfig({
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'hasDropdown',
})`
  color: ${props => props.isActive ? '#ff6b35' : '#ffffff'};
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    color: #ff6b35;
    background: rgba(255, 107, 53, 0.1);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    width: 0;
    height: 2px;
    background: #ff6b35;
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }

  ${props => props.isActive && `
    &::after {
      width: 80%;
    }
  `}

  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem 1.5rem;
    font-size: 1.1rem;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 0;
    min-height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &:first-child {
      border-top: 1px solid rgba(255, 255, 255, 0.05);
    }

    &:hover {
      background: rgba(255, 107, 53, 0.15);
    }

    &::after {
      display: none;
    }

    ${props => props.isActive && `
      background: rgba(255, 107, 53, 0.15);
      border-left: 3px solid #ff6b35;
    `}
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DropdownButton = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'isOpen',
})`
  color: ${props => props.isActive ? '#ff6b35' : '#ffffff'};
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: #ff6b35;
    background: rgba(255, 107, 53, 0.1);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    width: 0;
    height: 2px;
    background: #ff6b35;
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }

  ${props => props.isActive && `
    &::after {
      width: 80%;
    }
  `}

  svg {
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem 1.5rem;
    font-size: 1.1rem;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 0;
    min-height: 56px;
    justify-content: space-between;

    &:first-child {
      border-top: 1px solid rgba(255, 255, 255, 0.05);
    }

    &:hover {
      background: rgba(255, 107, 53, 0.15);
    }

    &::after {
      display: none;
    }

    ${props => props.isActive && `
      background: rgba(255, 107, 53, 0.15);
      border-left: 3px solid #ff6b35;
    `}
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 0.25rem;
  background: rgba(20, 20, 20, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.5rem 0;
  min-width: 180px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  overflow: hidden;
  pointer-events: auto;

  /* Create an invisible bridge area at the top to prevent gap issues */
  &::before {
    content: '';
    position: absolute;
    top: -0.5rem;
    left: 0;
    right: 0;
    height: 0.5rem;
    background: transparent;
    pointer-events: auto;
  }

  @media (max-width: 768px) {
    position: static;
    margin-top: 0;
    margin-left: 1.5rem;
    margin-right: 1.5rem;
    margin-bottom: 0.5rem;
    border-radius: 0;
    border-left: 3px solid rgba(255, 107, 53, 0.3);
    border-top: none;
    border-right: none;
    border-bottom: none;
    background: rgba(255, 255, 255, 0.03);
    box-shadow: none;

    &::before {
      display: none;
    }
  }
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 0.75rem 1.5rem;
  color: #ffffff;
  text-decoration: none;
  font-weight: 400;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;

  &:hover {
    background: rgba(255, 107, 53, 0.1);
    color: #ff6b35;
    border-left-color: #ff6b35;
  }

  ${props => props.isActive && `
    background: rgba(255, 107, 53, 0.15);
    color: #ff6b35;
    border-left-color: #ff6b35;
  `}

  @media (max-width: 768px) {
    padding: 0.875rem 1.5rem;
    font-size: 0.95rem;
  }
`;

const NavAndUserContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
    
    /* Hide desktop UserSection on mobile */
    > *:last-child {
      display: none !important;
    }
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem 0 0 0;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    margin-top: 1rem;
    display: ${props => props.hidden ? 'none' : 'flex'} !important;
  }
`;

const LoginButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    justify-content: center;
    min-height: 56px;
    border-radius: 8px;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 107, 53, 0.1);
  color: #ff6b35;
  border: 1px solid rgba(255, 107, 53, 0.3);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background: rgba(255, 107, 53, 0.2);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    justify-content: center;
    min-height: 56px;
    border-radius: 8px;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
  min-width: 48px;
  min-height: 48px;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
  }

  &:active {
    background: rgba(255, 255, 255, 0.15);
  }

  @media (min-width: 769px) {
    display: none !important;
  }

  @media (max-width: 768px) {
    display: flex !important;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);
  const [equipmentDropdownTimeout, setEquipmentDropdownTimeout] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoggedIn, isAdmin, isAuthor, isAdminOrAuthor, isBrand, logout } = useUser();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Close dropdown when mobile menu closes
    if (isMenuOpen) {
      setEquipmentDropdownOpen(false);
    }
  };

  const handleEquipmentMouseEnter = () => {
    // Clear any pending close timeout
    if (equipmentDropdownTimeout) {
      clearTimeout(equipmentDropdownTimeout);
      setEquipmentDropdownTimeout(null);
    }
    setEquipmentDropdownOpen(true);
  };

  const handleEquipmentMouseLeave = () => {
    // Add a small delay before closing to allow mouse to move to dropdown
    const timeout = setTimeout(() => {
      setEquipmentDropdownOpen(false);
    }, 300);
    setEquipmentDropdownTimeout(timeout);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (equipmentDropdownTimeout) {
        clearTimeout(equipmentDropdownTimeout);
      }
    };
  }, [equipmentDropdownTimeout]);

  const handleLogout = async () => {
    await logout();
    // Redirect to homepage after logout
    navigate('/');
  };

  const handleAdminClick = () => {
    if (isAdmin()) {
      navigate('/admin');
    }
  };

  const isEquipmentActive = location.pathname === '/equipment' || location.pathname === '/brands' || location.pathname.startsWith('/brand/');

  const navItems = [
    { path: '/', label: 'Home' },
    ...(isLoggedIn ? [{ path: '/compare', label: 'Compare' }] : []),
    { 
      path: '/equipment', 
      label: 'Equipment',
      hasDropdown: true,
      submenu: [
        { path: '/equipment', label: 'All Equipment' },
        { path: '/brands', label: 'Brands' }
      ]
    },
    { path: '/articles', label: 'Articles' },
    { path: '/studios', label: 'Studios' },
    ...(isLoggedIn ? [
      { path: '/my-studio', label: 'My Studio' }
    ] : [])
  ];


  return (
    <HeaderContainer
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Logo to="/">
        <Headphones size={32} />
        <span className="logo-text">Studio Gear Compare</span>
      </Logo>

      <NavAndUserContainer>
        <Nav isOpen={true}>
          {navItems.map((item) => {
            if (item.hasDropdown) {
              return (
                <DropdownContainer
                  key={item.path}
                  onMouseEnter={handleEquipmentMouseEnter}
                  onMouseLeave={handleEquipmentMouseLeave}
                >
                  <DropdownButton
                    isActive={isEquipmentActive}
                    isOpen={equipmentDropdownOpen}
                    onClick={(e) => {
                      e.preventDefault();
                      setEquipmentDropdownOpen(!equipmentDropdownOpen);
                    }}
                    onMouseEnter={handleEquipmentMouseEnter}
                  >
                    {item.label}
                    <ChevronDown size={16} />
                  </DropdownButton>
                  {equipmentDropdownOpen && (
                    <DropdownMenu
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                        handleEquipmentMouseEnter();
                      }}
                      onMouseLeave={handleEquipmentMouseLeave}
                    >
                      {item.submenu.map((subItem) => (
                        <DropdownItem
                          key={subItem.path}
                          to={subItem.path}
                          isActive={location.pathname === subItem.path}
                        >
                          {subItem.label}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  )}
                </DropdownContainer>
              );
            }
            return (
            <NavLink
              key={item.path}
              to={item.path}
              isActive={location.pathname === item.path}
            >
              {item.label}
            </NavLink>
            );
          })}
        </Nav>

        {/* UserSection only visible on desktop */}
        <UserSection>
          {isLoggedIn ? (
            <>
              <UserTierIndicator onClick={handleAdminClick} />
              <LogoutButton onClick={handleLogout}>
                <LogOut size={16} />
                Logout
              </LogoutButton>
            </>
          ) : (
            // Login button hidden during beta - use /beta-login route instead
            null
          )}
        </UserSection>
      </NavAndUserContainer>

      {/* Mobile Menu - rendered via portal to avoid stacking context issues */}
      {typeof document !== 'undefined' && ReactDOM.createPortal(
        <>
          {/* Overlay that closes menu when clicked */}
          <MobileMenuOverlay 
            isOpen={isMenuOpen} 
            onClick={() => setIsMenuOpen(false)}
          />
          
          {/* Close button inside menu */}
          {isMenuOpen && (
            <MobileMenuCloseButton 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsMenuOpen(false);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsMenuOpen(false);
              }}
            >
              <X size={24} />
            </MobileMenuCloseButton>
          )}
          
          <Nav isOpen={isMenuOpen} className="mobile-nav" onClick={(e) => e.stopPropagation()}>
            {navItems.map((item) => {
              if (item.hasDropdown) {
                return (
                  <DropdownContainer key={item.path}>
                    <DropdownButton
                      isActive={isEquipmentActive}
                      isOpen={equipmentDropdownOpen}
                      onClick={() => setEquipmentDropdownOpen(!equipmentDropdownOpen)}
                    >
                      {item.label}
                      <ChevronDown size={18} />
                    </DropdownButton>
                    {equipmentDropdownOpen && (
                      <DropdownMenu
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        {item.submenu.map((subItem) => (
                          <DropdownItem
                            key={subItem.path}
                            to={subItem.path}
                            isActive={location.pathname === subItem.path}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setEquipmentDropdownOpen(false);
                            }}
                          >
                            {subItem.label}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    )}
                  </DropdownContainer>
                );
              }
              return (
              <NavLink
                key={item.path}
                to={item.path}
                isActive={location.pathname === item.path}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </NavLink>
              );
            })}
            
            {/* UserSection only visible in mobile menu */}
            <UserSection className="mobile-user-section">
              {isLoggedIn ? (
                <>
                  <UserTierIndicator 
                    onClick={() => {
                      // Close menu after navigation
                      setTimeout(() => setIsMenuOpen(false), 200);
                    }}
                  />
                  <LogoutButton onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}>
                    <LogOut size={18} />
                    Logout
                  </LogoutButton>
                </>
              ) : (
                // Login button hidden during beta - use /beta-login route instead
                null
              )}
            </UserSection>
          </Nav>
        </>,
        document.body
      )}

      <MobileMenuButton onClick={toggleMenu}>
        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </MobileMenuButton>
    </HeaderContainer>
  );
};

export default Header;

